import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { CHAVE_PRODUTOR } from "@/hooks/use-mercado";

/**
 * Conferir os kg — as duas portas, um contrato só.
 *
 * ⚠️ Nenhum hook aqui calcula valor. `valorConferido` chega pronto do servidor,
 * que o recalcula com kg × R$/kg conferidos. O total que o formulário mostra
 * enquanto se digita é rascunho de exibição; o que vale é a resposta.
 */

export interface DadosConferencia {
  bate: boolean;
  /** kg e R$/kg do comprador (desde 24/09 o preço também se confere). */
  itens?: Array<{ variedade: string; qualidade: string | null; kg: number; precoKg?: number }>;
  observacao?: string;
  /**
   * SÓ no app: o vencimento conferido (`null` = a definir). Ausente = não mexe.
   * O link público não tem o campo — o servidor nem o aceita lá.
   */
  vencimento?: string | null;
}

export interface RespostaConferencia {
  statusCarga: "CONFERIDO" | "AJUSTADO";
  statusPagamento: "PAGO" | "A_PAGAR";
  bate: boolean;
  totalKgRomaneio: number;
  totalKgConferido: number;
  valorRomaneio: number;
  valorConferido: number;
}

/** Pelo LINK PÚBLICO: sem sessão, a defesa é o token. */
export function useConferirPublico(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosConferencia) =>
      apiRequest("POST", `/api/mercado/publico/${token}/conferir`, dados).then(r => r.json()) as Promise<RespostaConferencia>,
    // Recarrega o próprio romaneio: o bloco passa a mostrar o resumo com o valor
    // que o SERVIDOR gravou, não o que a tela somou enquanto se digitava.
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/mercado/publico", token] }); },
  });
}

/** Pelo APP do comprador: mesma função de domínio, sessão no lugar do token. */
export function useConferirComprador(compartilhamentoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosConferencia) =>
      apiRequest("POST", `/api/mercado/comprador/romaneios/${compartilhamentoId}/conferir`, dados)
        .then(r => r.json()) as Promise<RespostaConferencia>,
    onSuccess: () => {
      // O detalhe, a lista (o chip muda) e o Início (a pendência "sem conferir"
      // cai, e o valor conferido muda o custo da carga). O vencimento conferido
      // muda a agenda do A pagar.
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/romaneios"] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/home"] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/inicio"] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/pagar"] });
    },
  });
}

/**
 * Vencimento — os dois lados escrevem no mesmo campo, cada um pela sua rota.
 *
 * Sem aceite: o último que escreveu vale. Invalida os DOIS lados porque o número
 * mudou para ambos, e também as queries do Cacho: o prazo mora na VENDA (061), e
 * é ele que sai no PDF, na imagem e no texto do WhatsApp.
 *
 * ⚠️ O `id` MUDA de significado com o lado: para o produtor é o da VENDA (é a
 * chave que existe mesmo sem formalizar — foi o que destravou os 15 romaneios
 * sem prazo); para o comprador é o do COMPARTILHAMENTO, que é o que ele conhece
 * e o que prova que o romaneio é dele.
 *
 * `vencimento: null` LIMPA — volta para "a definir".
 */
export function useAlterarVencimento(lado: "produtor" | "comprador") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { id: string; vencimento: string | null }) =>
      apiRequest(
        "PATCH",
        lado === "produtor"
          ? `/api/mercado/produtor/receber/${dados.id}/vencimento`
          : `/api/mercado/comprador/romaneios/${dados.id}/vencimento`,
        { vencimento: dados.vencimento },
      ).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/romaneios"] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/home"] });
      // O vencimento é o eixo do fluxo de caixa do Início — mudá-lo move a
      // dívida de semana.
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/inicio"] });
      qc.invalidateQueries({ queryKey: ["/api/vendas"] });
    },
  });
}
